<template>
  <hr-menu-item
    v-if="loading"
    :label="i18n.t('common.loading')"
  >
    <template #leading>
      <hr-loading-spinner />
    </template>
  </hr-menu-item>
  <template v-else>
    <hr-list-section-header v-if="userViews.length">
      {{ i18n.t('tableViews.userViewsTitle') }}
    </hr-list-section-header>
    <hr-menu-item
      v-for="tableView in userViews"
      :key="tableView.id"
      v-close-popup
      :label="tableView.tableViewTitle"
      :icon="bookmarkIcon(tableView)"
      :active="currentViewId === tableView.id"
      :data-e2e="`list-item-table-view-${toE2eId(tableView.tableViewTitle)}`"
      @click="emit('load-view', tableView)"
    >
      <template #trailing>
        <hr-icon-action-btn
          icon="edit"
          :aria-label="i18n.t('tableViews.buttonEditView', { tableViewTitle: tableView.tableViewTitle })"
          :data-e2e="`button-edit-table-view-${toE2eId(tableView.tableViewTitle)}`"
          @click.stop="emit('edit-view', tableView)"
        />
      </template>
    </hr-menu-item>

    <hr-list-section-header v-if="clinicViews.length">
      {{ i18n.t('tableViews.clinicViewsTitle') }}
    </hr-list-section-header>
    <hr-menu-item
      v-for="tableView in clinicViews"
      :key="tableView.id"
      v-close-popup
      :label="tableView.tableViewTitle"
      :icon="bookmarkIcon(tableView)"
      :active="currentViewId === tableView.id"
      :data-e2e="`list-item-table-view-${toE2eId(tableView.tableViewTitle)}`"
      @click="emit('load-view', tableView)"
    >
      <template
        v-if="canEditClinicViews"
        #trailing
      >
        <hr-icon-action-btn
          icon="edit"
          :aria-label="i18n.t('tableViews.buttonEditView', { tableViewTitle: tableView.tableViewTitle })"
          :data-e2e="`button-edit-table-view-${toE2eId(tableView.tableViewTitle)}`"
          @click.stop="emit('edit-view', tableView)"
        />
      </template>
    </hr-menu-item>
    <!--
      The collection itself, listed among the views because that is where a reader looks for it.
      Picking a view is easy and getting back out of one had no answer at all: Reset returns to the
      view as saved, which is the opposite of leaving it.
    -->
    <template v-if="defaultViewLabel !== null">
      <hr-list-section-header>
        {{ i18n.t('tableViews.defaultViewsTitle') }}
      </hr-list-section-header>
      <hr-menu-item
        v-close-popup
        icon="format_list_bulleted"
        :label="defaultViewLabel"
        :active="currentViewId === null"
        data-e2e="list-item-default-view"
        @click="emit('load-default')"
      />
    </template>
  </template>

  <hr-divider v-if="userViews.length || clinicViews.length || defaultViewLabel !== null" />
  <hr-menu-item
    v-if="canSaveToCurrentView"
    v-close-popup
    icon="check"
    :label="i18n.t('tableViews.saveToThisView')"
    data-e2e="button-save-to-current-view"
    @click="emit('save-to-current-view')"
  />
  <hr-menu-item
    v-close-popup
    :label="i18n.t('tableViews.saveView')"
    icon="save"
    data-e2e="button-save-current-view"
    @click="emit('save-new-view')"
  />
  <template v-if="isEdited">
    <hr-divider />
    <hr-menu-item
      v-close-popup
      icon="undo"
      :label="i18n.t('tableViews.discardChanges')"
      data-e2e="button-discard-view-changes"
      @click="emit('discard-changes')"
    />
  </template>
</template>

<script setup>
import { i18n } from 'boot/i18n';

import {
  HrDivider,
  HrIconActionBtn,
  HrListSectionHeader,
  HrLoadingSpinner,
  HrMenuItem,
} from 'src/design-system';

/**
 * The rows inside the saved-views menu: the views themselves, and what can be done with the state
 * currently on screen.
 *
 * Separate from TableViews because the panel these rows sit in is no longer always the same panel.
 * The bookmark trigger opens them in a menu it owns; the records headline opens them in a menu
 * HrTitleMenuBtn owns. The rows are the same either way, so they are a component rather than a
 * block of markup written out once per shell.
 *
 * It holds no state and reaches nothing. Every row reports what was pressed and lets TableViews,
 * which owns the views and the writes, decide what that means.
 */
defineOptions({ name: 'TableViewsMenu' });

const props = defineProps({
  /** The views are still being read. Nothing else in the menu is known yet. */
  loading: {
    type: Boolean,
    default: false,
  },
  /** The reader's own saved views. */
  userViews: {
    type: Array,
    default: () => ([]),
  },
  /** Views shared across the clinic that this reader may see. */
  clinicViews: {
    type: Array,
    default: () => ([]),
  },
  /** Id of the view the table is currently showing, or null when it is showing none. */
  currentViewId: {
    type: String,
    default: null,
  },
  /** Whether this reader may edit a shared view, which is what puts a pencil on its row. */
  canEditClinicViews: {
    type: Boolean,
    default: false,
  },
  /** Whether the state on screen can be written back into the view it came from. */
  canSaveToCurrentView: {
    type: Boolean,
    default: false,
  },
  /** Whether what is on screen has diverged from the view it was loaded from. */
  isEdited: {
    type: Boolean,
    default: false,
  },
  /**
   * The collection's own name, for the row that leaves every view behind and shows it whole. Null
   * on a collection with no defaults to go back to, which takes the row away with it.
   */
  defaultViewLabel: {
    type: String,
    default: null,
  },
});

const emit = defineEmits({
  /** A view was chosen; payload is the view document. */
  'load-view': null,
  /** A view's pencil was pressed; payload is the view document. */
  'edit-view': null,
  /** The reader asked to store what is on screen as a new view. */
  'save-new-view': null,
  /** The reader asked to write what is on screen back into the loaded view. */
  'save-to-current-view': null,
  /** The reader asked to go back to what the loaded view stores. */
  'discard-changes': null,
  /** The reader asked to leave every view and see the collection itself. */
  'load-default': null,
});

const bookmarkIcon = (tableView) => (
  props.currentViewId === tableView.id ? 'bookmark' : 'bookmark_border'
);

const toE2eId = (tableViewTitle) => tableViewTitle.replace(/ /g, '-');
</script>
